import { Component, Input } from '@angular/core';

import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

type Indicator = {
    strengthThreshold: number,
    theme: 'primary' | 'accent' | 'warn',
    label: string
}

@Component({
    standalone: true,
    selector: 'sfx-password-meter',
    templateUrl: './password-meter.component.html',
    imports: [
        MatProgressBarModule,
        MatDividerModule,
        CommonModule
    ],
    styleUrls: ['./password-meter.component.scss']
})
export class PasswordMeterComponent {

    private _strength = 0;
    private _showStrength = true;
    private _theme = 'warn';
    private _colorLabel = '';

    private _showError = true;

    private _indicators: Indicator[] = [];
    private _currentIndicator: Indicator = { strengthThreshold: 0, theme: 'warn', label: '' };

    @Input()
    get showError(): boolean {
        return this._showError;
    }
    set showError(value: boolean | string) {
        this._showError = typeof value === 'boolean' ? value : !!(value === '' || value);
    }

    @Input()
    get indicators(): Indicator[] {
        return this._indicators;
    }
    set indicators(value: Indicator[] | string) {
        // Ignore - angularJS error, not angular2.
        // eslint-disable-next-line angular/typecheck-string
        if (typeof value === 'string') {
            return;
        }

        // Translate labels if provided
        this._indicators = value.map((indicator) => ({
            ...indicator,
            label: this.translate.instant(indicator.label)
        }));

        this._indicators = this._indicators.sort((a, b) => (a.strengthThreshold > b.strengthThreshold ? -1 : 1));
    }

    @Input()
    get strength(): number {
        return this._strength;
    }
    set strength(value: number | string) {
        // Ignore - angularJS error, not angular2.
        // eslint-disable-next-line angular/typecheck-number
        this._strength = typeof value === 'number' ? value : Number(value);
        this._currentIndicator = this._findCurrentIndicator();
    }

    @Input()
    get showStrength(): boolean {
        return this._showStrength;
    }
    set showStrength(value: boolean) {
        this._showStrength = value;
    }

    private _findCurrentIndicator(): Indicator {
        for (const indicator of this._indicators) {
            if (indicator.strengthThreshold <= this._strength) {
                return indicator;
            }
        }

        return this._currentIndicator;
    }

    get Theme(): 'primary' | 'accent' | 'warn' {
        return this._currentIndicator.theme;
    }

    get Label(): string {
        return this._currentIndicator.label;
    }

    constructor(private translate: TranslateService) {
        // Initialize indicators with translated labels
        this._indicators = [
            {
                strengthThreshold: 0,
                theme: 'warn',
                label: this.translate.instant('Very Weak')
            },
            {
                strengthThreshold: 25,
                theme: 'warn',
                label: this.translate.instant('Weak')
            },
            {
                strengthThreshold: 50,
                theme: 'accent',
                label: this.translate.instant('Moderate')
            },
            {
                strengthThreshold: 75,
                theme: 'primary',
                label: this.translate.instant('Strong')
            },
            {
                strengthThreshold: 100,
                theme: 'primary',
                label: this.translate.instant('Very Strong')
            }
        ];
        this._indicators = this._indicators.sort((a, b) => (a.strengthThreshold > b.strengthThreshold ? -1 : 1));
        this._currentIndicator = this._indicators[0];
    }
}
